import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  Tabs,
  tabButtonId,
  tabPanelId,
} from "@studio3t/generic-components";
import { useId, useRef, useState } from "react";
import styled from "styled-components";
import { useJobWorkersPolling } from "@/hooks/useApi";
import type { Checkpoint, LogEntry, ResourceRef } from "@/types/api";
import { CheckpointsBody } from "./CheckpointsSection";
import { currentCheckpoints } from "./checkpointView";
import { LogsBody } from "./LogsSection";
import { ScriptCards } from "./ScriptCard";
import { Section } from "./styles";
import { WorkersBody } from "./WorkersSection";

const CARDS = designVariables.ui.cards;
const SEMANTIC = designVariables.ui.semantic;

type TabId = "scripts" | "workers" | "checkpoints" | "logs";

// Countable tabs always show their count once it's known — including 0 —
// and stay bare only while the number is genuinely unknown (load in flight).
function countBadge(count: number | null) {
  if (count === null) return undefined;
  return <BadgeSecondarySubtle>{count}</BadgeSecondarySubtle>;
}

// The default is the first tab on the bar — Scripts once the definition
// resolves any, Workers otherwise; a user's own (still valid) choice wins.
function resolveActiveTab(chosen: TabId | null, hasScripts: boolean): TabId {
  const firstTab = hasScripts ? "scripts" : "workers";
  if (chosen === null) return firstTab;
  // A chosen Scripts tab that lost its scripts falls back to the bar's first.
  if (chosen === "scripts") return hasScripts ? chosen : firstTab;
  return chosen;
}

function buildTabs({
  hasScripts,
  scriptCount,
  workers,
  isRunning,
  checkpoints,
  checkpointsLoading,
}: {
  hasScripts: boolean;
  scriptCount: number;
  workers: { worker_count: number } | null | undefined;
  isRunning: boolean;
  checkpoints: Checkpoint[] | null | undefined;
  checkpointsLoading: boolean;
}) {
  // A stopped pipeline has no workers by definition — its polling is
  // disabled, so count the certain 0 rather than waiting for data. A
  // settled checkpoints query with nothing to show counts as 0; only an
  // in-flight one leaves the tab uncounted.
  const workerCount = workers ? workers.worker_count : isRunning ? null : 0;
  const checkpointCount = checkpointsLoading
    ? null
    : currentCheckpoints(checkpoints ?? []).current.length;
  return [
    ...(hasScripts
      ? [
          {
            id: "scripts",
            label: "Scripts",
            badge: countBadge(scriptCount),
          },
        ]
      : []),
    { id: "workers", label: "Workers", badge: countBadge(workerCount) },
    {
      id: "checkpoints",
      label: "Checkpoints",
      badge: countBadge(checkpointCount),
    },
    { id: "logs", label: "Logs" },
  ];
}

// The panels are not focus stops of their own: from the bar, Tab goes
// straight to the first control inside the active panel (the script
// card's Copy, the Workers table's sort headers, Logs' "View all logs"),
// per the ARIA tabs pattern — a panel takes tabindex only when it holds
// nothing focusable, and Checkpoints, which holds none, is content to
// read rather than a stop to land on.
// The lower pipeline-detail sections (Scripts, Workers, Checkpoints, Logs)
// collapsed into a tabbed section (Figma 13806-30145 / 15544-191510). The tab
// bar is not a card of its own: it has no border except its bottom edge, which
// is shared as the top border of the content card below, where the active
// tab's body renders. (The Logs body carries its own "View all logs" button —
// see LogsBody.) Scripts is the exception both ways: the tab only exists when
// the pipeline stages scripts, and its cards sit detached below the bar
// (Figma 15544-191510) instead of in the attached panel.
export function SectionTabs({
  jobName,
  isRunning,
  scripts,
  checkpoints,
  checkpointsLoading,
  logs,
  logsLoading,
}: {
  jobName: string;
  isRunning: boolean;
  /** The pipeline's staged transform scripts, in stage order. */
  scripts: ResourceRef[];
  checkpoints: Checkpoint[] | null | undefined;
  // True while the checkpoints query is in flight — the tab body shows a
  // skeleton instead of the "no checkpoints" empty state.
  checkpointsLoading: boolean;
  logs: LogEntry[];
  logsLoading: boolean;
}) {
  const hasScripts = scripts.length > 0;
  // Deferred rather than initialized, since the scripts arrive async.
  const [chosen, setChosen] = useState<TabId | null>(null);
  const active = resolveActiveTab(chosen, hasScripts);
  const rootRef = useRef<HTMLElement>(null);
  const idBase = useId();
  const {
    data: workers,
    isLoading: workersLoading,
    error: workersError,
  } = useJobWorkersPolling(jobName, 5000, isRunning);

  // On tab change, scroll the page to the bottom so the selected section
  // is fully in view.
  const selectTab = (id: TabId) => {
    setChosen(id);
    scrollPageToBottom(rootRef.current);
  };

  const tabs = buildTabs({
    hasScripts,
    scriptCount: scripts.length,
    workers,
    isRunning,
    checkpoints,
    checkpointsLoading,
  });

  return (
    <Section ref={rootRef}>
      <Bar
        tabs={tabs}
        activeId={active}
        onChange={(id) => selectTab(id as TabId)}
        label="Pipeline sections"
        idBase={idBase}
      />
      {active === "scripts" && (
        <ScriptsPanel
          role="tabpanel"
          id={tabPanelId(idBase, active)}
          aria-labelledby={tabButtonId(idBase, active)}
        >
          <ScriptCards scripts={scripts} />
        </ScriptsPanel>
      )}
      {active !== "scripts" && (
        <Panel
          role="tabpanel"
          id={tabPanelId(idBase, active)}
          aria-labelledby={tabButtonId(idBase, active)}
        >
          {active === "workers" && (
            <WorkersBody
              data={workers}
              loading={workersLoading}
              error={workersError}
              isRunning={isRunning}
            />
          )}
          {active === "checkpoints" && (
            <CheckpointsBody
              checkpoints={checkpoints}
              loading={checkpointsLoading}
            />
          )}
          {active === "logs" && (
            <LogsBody jobName={jobName} logs={logs} loading={logsLoading} />
          )}
        </Panel>
      )}
    </Section>
  );
}

// The app's page scroller is an OverlayScrollbars viewport (an ancestor of
// the tabbed card); it ignores scrollTo({behavior:"smooth"}), so set
// scrollTop directly. Two frames let the new tab content lay out first.
function scrollPageToBottom(root: HTMLElement | null) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const target =
        root?.closest("[data-overlayscrollbars-viewport]") ??
        document.scrollingElement;
      if (target) target.scrollTop = target.scrollHeight;
    });
  });
}

// The tab's focus ring is drawn outside its box (see the library's tab
// tokens), so its bottom stroke lies in the panel just below the hairline;
// the bar stacks above the panel so nothing positioned in there (a table's
// sticky header cells) paints over it.
const Bar = styled(Tabs)`
  position: relative;
  z-index: 1;
`;

// The Scripts tab's body: standalone script cards detached from the bar by the
// section gap (Figma 15544-191510), unlike the other tabs' attached panel.
const ScriptsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacingl}px;
  padding-top: ${SEMANTIC.spacingl}px;
`;

// Content card below the tab bar. Its top edge is the bar's shared bottom
// border, so it carries only side + bottom borders and rounds its bottom corners.
const Panel = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-top: none;
  border-bottom-left-radius: ${CARDS.corner}px;
  border-bottom-right-radius: ${CARDS.corner}px;
  overflow: hidden;
`;
